import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import {
  useParams,
} from 'react-router';
import {
  Submenu,
  color,
} from 'vibeguide';
import {
  get,
  isEmpty,
  trim,
} from 'lodash';
import ProfileInformation from './Profile/Sections/ProfileInformation';
import ProfilePlayer from './Profile/Sections/ProfilePlayer';

function ProfileContainer({
  user,
  currentLocation,
}) {
  if (!currentLocation) {
    return null;
  }

  const [location, setLocation] = useState(currentLocation);
  const [isDisabled, setIsDisabled] = useState(false);

  // Normalize the resolution / display settings for a visual location so the
  // form has the shape it expects. The new API contract nests the full screen
  // resolution object inside `displaySettings.resolution`; legacy payloads may
  // still surface `resolution` (or `resolutionSize`) at the top level, so
  // hoist those into the canonical nested shape here.
  useEffect(() => {
    if (get(currentLocation, 'mediaFormat', '') !== 'visual') {
      setLocation(currentLocation);
      return;
    }

    const rawDisplaySettings = currentLocation.displaySettings || {};
    let nestedResolution = rawDisplaySettings.resolution;

    if (!nestedResolution && currentLocation.resolution) {
      if (typeof currentLocation.resolution === 'object') {
        nestedResolution = {
          _id: currentLocation.resolution._id,
          name: currentLocation.resolution.name || '',
          size: currentLocation.resolution.size
            || currentLocation.resolutionSize
            || [0, 0],
          custom: Boolean(currentLocation.resolution.custom),
        };
      } else {
        nestedResolution = {
          name: currentLocation.resolution,
          size: currentLocation.resolutionSize || [0, 0],
          custom: typeof currentLocation.resolution === 'string'
            && currentLocation.resolution.toLowerCase().startsWith('custom'),
        };
      }
    }

    const {
      resolution: _legacyResolution,
      resolutionSize: _legacyResolutionSize,
      ...rest
    } = currentLocation;

    setLocation({
      ...rest,
      displaySettings: {
        resolution: { name: '', size: [0, 0] },
        screenDimensions: ['', ''],
        screenDiagonal: '',
        screenRotation: 0,
        contentRotation: 0,
        advancedEnabled: false,
        displayAreaDimensions: [0, 0],
        displayAreaPosition: [0, 0],
        ...rawDisplaySettings,
        ...(nestedResolution ? { resolution: nestedResolution } : {}),
      },
    });
  }, [currentLocation.resolution, currentLocation.resolutionSize, currentLocation.displaySettings]);

  const {
    id,
    type,
  } = useParams();

  /**
   * Update the location
   */
  const onUpdateLocation = (data) => {
    const newLocation = {
      ...location,
      ...data,
    };

    const visualDisplaySettings = newLocation.displaySettings || {};
    const hasVisualIssue = newLocation.mediaFormat === 'visual' ? (
      !visualDisplaySettings.resolution
      || isEmpty(trim(visualDisplaySettings.resolution.name || ''))
      || visualDisplaySettings.screenRotation === undefined
      || visualDisplaySettings.screenRotation === null
      || visualDisplaySettings.contentRotation === undefined
      || visualDisplaySettings.contentRotation === null
    ) : false;

    const disabled = isEmpty(trim(newLocation.name))
      || isEmpty(trim(newLocation.address1))
      || isEmpty(trim(newLocation.postalCode))
      || isEmpty(trim(newLocation.city))
      || isEmpty(trim(newLocation.country))
      || ((newLocation.country === 'US' || newLocation.country === 'CA') && !newLocation.state)
      || isEmpty(trim(newLocation.companyId))
      || isEmpty(trim(newLocation.category))
      || isEmpty(trim(newLocation.bannerName))
      || hasVisualIssue
      || isEmpty(trim(newLocation.contentNetworkOwner))
      || isEmpty(trim(newLocation.contentDeliverySystem))
      || (newLocation.adNetworkEnabled && isEmpty(trim(newLocation.adDeliverySystem)))
      || (newLocation.adNetworkEnabled && isEmpty(trim(newLocation.adNetworkOwner)));

    setIsDisabled(disabled);
    setLocation(newLocation);
  };

  /**
   * Get the page content
   */
  const getPageContent = () => {
    switch (type) {
      case 'player':
        return (
          <ProfilePlayer
            location={location}
            user={user}
            onUpdate={onUpdateLocation}
          />
        );

      case 'information':
      default:
        return (
          <ProfileInformation
            location={location}
            user={user}
            onUpdate={onUpdateLocation}
            isDisabled={isDisabled}
          />
        );
    }
  };

  return (
    <div
      className="ProfileContainer"
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100% - 150px)',
        overflow: 'auto',
      }}
    >
      <Submenu
        rootUrl={`/location/${id}/profile`}
        style={{
          position: 'sticky',
          top: 0,
          padding: '0 24px',
          // borderBottom: `1px solid ${color.whiteSmoke}`,
          backgroundColor: color.lightGray,
          zIndex: 2,
        }}
        items={[
          {
            name: 'Information',
            show: true,
            enabled: true,
          },
          {
            name: 'Player',
            show: user.can('location.modify_volume')
              || user.can('location.view_device_config'),
            enabled: true,
          },
        ]}
      />

      {getPageContent()}
    </div>
  );
}

function mapStateToProps(state) {
  return {
    user: state.login.user,
    currentLocation: state.locations.currentLocation,
  };
}

export default connect(mapStateToProps)(ProfileContainer);
